import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BrandButton } from "@/components/PageHeader";
import { Field } from "@/components/event-setup/BasicsSection";
import { SponsorLogo } from "@/components/SponsorsCarousel";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", logo: "", phone: "", email: "", website: "", active: true };

const LogoPicker = ({ f, set }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      set({ logo: (await api.post("/uploads/banner?kind=logo", fd)).data.url });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-4">
      <div className="h-20 w-40 grid place-items-center border border-dashed border-[#D1D5DB] rounded-lg bg-[#F8F9FA]" data-testid="sponsor-logo-preview">
        {f.logo ? <SponsorLogo s={f} /> : <span className="text-xs text-gray-400">No logo yet</span>}
      </div>
      <div className="flex flex-col gap-2">
        <BrandButton type="button" variant="outline" onClick={() => ref.current.click()} disabled={busy} data-testid="sponsor-logo-upload-button"><Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload logo"}</BrandButton>
        {f.logo && <button type="button" onClick={() => set({ logo: "" })} className="text-xs text-gray-500 hover:text-[#E60000] inline-flex items-center gap-1" data-testid="sponsor-logo-remove"><X className="w-3 h-3" />Remove logo</button>}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="sponsor-logo-file-input" />
    </div>
  );
};

export const SponsorDialog = ({ sponsor, open, onClose, onSaved, onSubmit, showLabel = "Show on homepage" }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const txt = (k) => (e) => set({ [k]: e.target.value });
  useEffect(() => {
    if (open) { setF(sponsor ? { ...EMPTY, ...sponsor } : EMPTY); }
  }, [open, sponsor]);
  const save = async () => {
    if (f.name.trim().length < 2) return toast.error("Enter the sponsor's name");
    if (f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("Enter a valid sponsor email address");
    if (f.website.trim() && !/^https?:\/\/\S+$/i.test(f.website.trim())) return toast.error("Website must start with https://");
    setBusy(true);
    try {
      const body = { name: f.name, logo: f.logo, phone: f.phone, email: f.email, website: f.website, active: f.active };
      if (onSubmit) onSubmit(body);
      else {
        await (sponsor ? api.put(`/sponsors/${sponsor.id}`, body) : api.post("/sponsors", body));
        toast.success(sponsor ? "Sponsor updated" : "Sponsor added");
        onSaved();
      }
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sponsor-dialog">
        <DialogHeader><DialogTitle className="font-heading">{sponsor ? "Edit sponsor" : "Add sponsor"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <LogoPicker f={f} set={set} />
          <Field label="Sponsor name"><Input value={f.name} onChange={txt("name")} data-testid="sponsor-name-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contact number"><Input value={f.phone} onChange={txt("phone")} placeholder="+233 …" data-testid="sponsor-phone-input" /></Field>
            <Field label="Email address"><Input value={f.email} onChange={txt("email")} data-testid="sponsor-email-input" /></Field>
          </div>
          <Field label="Website (optional)"><Input value={f.website} onChange={txt("website")} placeholder="https://" data-testid="sponsor-website-input" /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set({ active: v })} data-testid="sponsor-active-toggle" />{showLabel}</label>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <BrandButton variant="outline" onClick={onClose} data-testid="sponsor-cancel-button">Cancel</BrandButton>
          <BrandButton onClick={save} disabled={busy} data-testid="sponsor-save-button">{busy ? "Saving…" : "Save sponsor"}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
